import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MILK_SESSIONS, sessionsSchema, type MilkSession } from '@dairy/shared';
import { useSettings } from '@/auth/useSession';
import { Button, Card, Checkbox, Input, PageHeader } from '@/components/ui';
import { useSaveTenant } from './useTenantSettings';

const DEFAULT_TIMES: Record<MilkSession, string> = { MORNING: '06:00', MIDDAY: '12:00', EVENING: '17:00' };

/** MR-2 / S2-T08: enable 1–3 sessions in day order with times; changing sessions never changes past records. */
export function SessionsSettingsPage() {
  const { t } = useTranslation();
  const s = useSettings();
  const { saveTenant, saving } = useSaveTenant();
  const [state, setState] = useState(() =>
    Object.fromEntries(
      MILK_SESSIONS.map((m) => {
        const cur = s.sessions.find((x) => x.session === m);
        return [m, { enabled: !!cur, time: cur?.time ?? DEFAULT_TIMES[m] }];
      }),
    ) as Record<MilkSession, { enabled: boolean; time: string }>,
  );
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const sessions = MILK_SESSIONS.filter((m) => state[m].enabled).map((m) => ({ session: m, time: state[m].time }));
    const r = sessionsSchema.safeParse(sessions);
    if (!r.success) return setError(t('settings.sessionsInvalid'));
    setError(null);
    await saveTenant({ settings: { sessions: r.data } });
  };
  return (
    <div className="mx-auto max-w-lg space-y-4">
      <PageHeader title={t('settings.sessions')} />
      <Card className="space-y-3">
        {MILK_SESSIONS.map((m) => (
          <div key={m} className="flex items-center gap-3">
            <Checkbox className="flex-1" label={t(`milk.session.${m}`)} checked={state[m].enabled} onChange={(e) => setState({ ...state, [m]: { ...state[m], enabled: e.target.checked } })} />
            <Input type="time" className="w-36" value={state[m].time} disabled={!state[m].enabled} onChange={(e) => setState({ ...state, [m]: { ...state[m], time: e.target.value } })} aria-label={t(`milk.session.${m}`)} />
          </div>
        ))}
        <p className="text-sm text-stone-600">{t('settings.sessionsHelp')}</p>
        {error ? <p className="text-red-700">{error}</p> : null}
        <Button disabled={saving} onClick={() => void submit()}>
          {t('common.save')}
        </Button>
      </Card>
    </div>
  );
}
